// PatientActionsBar.jsx
import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  Paper,
  Stack,
  Button,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Alert,
} from "@mui/material";

import BoltIcon from "@mui/icons-material/Bolt";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddTaskIcon from "@mui/icons-material/AddTask";

// Tasks module — diálogo real conectado a POST /api/ClinicalOperations/tasks
import TaskFormDialog from "../../../../modules/tasks/components/TaskFormDialog";
import { createTask as createTaskApi } from "../../../../modules/tasks/services/tasksService";
import useAuthUser from "../../../../hooks/useAuthUser";
import {
  getEmployees,
  getLocations,
} from "../../../../utils/services/control/masterDataService";

export default function PatientActionsBar({
  displayPatient,
  advancedMdPatientId,

  // ✅ IMPORTANT: esta debe ser la ruta que renderiza CreateClinicalNotePage
  addClinicalNotePath = "/CreateClinicalNotePage",

  quickActions = [
    { label: "Generate RAF", onClick: null, disabled: false },
    { label: "Generate HEDIS", onClick: null, disabled: false },
  ],
}) {
  const navigate = useNavigate();
  const currentUser = useAuthUser();

  const [anchorEl, setAnchorEl] = React.useState(null);
  const menuOpen = Boolean(anchorEl);

  // Task form (real, conectado al backend)
  const [taskFormOpen, setTaskFormOpen] = React.useState(false);
  const [taskSubmitError, setTaskSubmitError] = React.useState(null);
  const [employees, setEmployees] = React.useState([]);
  const [locations, setLocations] = React.useState([]);

  // Snackbar de feedback
  const [snackbar, setSnackbar] = React.useState(null); // { severity, message }

  const cipId = displayPatient?.cipId || "";
  const patientName =
    displayPatient?.fullName ||
    displayPatient?.patientFullName ||
    displayPatient?.name ||
    [displayPatient?.firstName, displayPatient?.lastName].filter(Boolean).join(" ") ||
    "";

  // Cargar employees + locations al montar (los necesita el TaskFormDialog)
  React.useEffect(() => {
    let cancelled = false;
    Promise.all([getEmployees({ status: 1 }), getLocations()])
      .then(([emps, locs]) => {
        if (cancelled) return;
        setEmployees(emps || []);
        setLocations(locs || []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // ✅ CreateClinicalNotePage necesita patientId (AdvancedMD)
  const disabledNoPatientForClinicalNote = !advancedMdPatientId;
  const disabledNoPatientForInternal = !cipId;

  // Add Task y Quick Action quedan ACTIVOS — usan el módulo real /tasks.
  // Los demás botones (Add Care Plan, More) siguen "Coming soon".
  const ENABLE_ONLY_CLINICAL_NOTE = true;

  const handleOpenTaskForm = () => {
    setTaskSubmitError(null);
    setTaskFormOpen(true);
  };

  const handleSubmitTask = async (data) => {
    setTaskSubmitError(null);
    try {
      const res = await createTaskApi({
        title: data.title,
        description: data.description,
        category: data.category,
        priority: data.priority,
        dueDate: data.dueDate,
        cipId: data.cipId,
        patientName: data.patientName,
        locationId: data.locationId,
        relatedType: data.relatedType,
        assigneeId: data.assigneeId,
        creatorId: currentUser?.employeeId || currentUser?.employee || null,
      });
      if (res?.result === 1) {
        setTaskSubmitError(res.message || "Could not create task.");
        return;
      }
      setTaskFormOpen(false);
      setSnackbar({ severity: "success", message: "Task assigned successfully." });
    } catch (err) {
      setTaskSubmitError(
        err?.response?.data?.message ||
          err?.response?.data?.title ||
          err?.message ||
          "Could not create task."
      );
    }
  };

  const ensureAbsolutePath = React.useCallback((p) => {
    if (!p) return "/";
    return p.startsWith("/") ? p : `/${p}`; // ✅ evita /clinical-information/...
  }, []);

  const goToClinicalNotes = React.useCallback(() => {
    if (!advancedMdPatientId) return;

    navigate(ensureAbsolutePath(addClinicalNotePath), {
      state: {
        patientId: String(advancedMdPatientId),
        patient: displayPatient || null,
        cipId: cipId || "",
      },
    });
  }, [advancedMdPatientId, navigate, addClinicalNotePath, displayPatient, cipId, ensureAbsolutePath]);

  // Navega a /care-plan con el paciente precargado para crear un care plan.
  // CarePlansPage lee location.state.patient y llama handlePatientSelect.
  const handleAddCarePlan = React.useCallback(() => {
    if (!cipId) return;
    navigate("/care-plan", {
      state: {
        patient: {
          memberId: cipId,
          memberFullName: patientName || "",
        },
      },
    });
  }, [navigate, cipId, patientName]);

  // Add Task, Quick Action y Add Care Plan ahora SÍ funcionan: solo
  // dependen de tener cipId. El "More" sigue "Coming soon".
  const disabledAddTask = disabledNoPatientForInternal;
  const disabledQuickAction = disabledNoPatientForInternal;
  const disabledCarePlan = disabledNoPatientForInternal;
  const disabledMore = ENABLE_ONLY_CLINICAL_NOTE;

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 3,
          px: 1,
          py: 0.75,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
          <Tooltip
            title={
              disabledNoPatientForInternal
                ? "Patient missing — cannot assign a task"
                : "Create a task for this patient"
            }
          >
            <span>
              <Button
                size="small"
                variant="contained"
                startIcon={<AssignmentTurnedInIcon />}
                onClick={handleOpenTaskForm}
                disabled={disabledAddTask}
              >
                Add Task
              </Button>
            </span>
          </Tooltip>

          <Tooltip
            title={
              disabledNoPatientForInternal
                ? "Patient missing — quick actions unavailable"
                : "Quick actions for this patient"
            }
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<BoltIcon />}
                onClick={(e) => {
                  if (disabledQuickAction) return;
                  setAnchorEl(e.currentTarget);
                }}
                disabled={disabledQuickAction}
              >
                Quick Action
              </Button>
            </span>
          </Tooltip>

          {/* ✅ Add Clinical Note */}
          <Tooltip
            title={
              disabledNoPatientForClinicalNote
                ? "Missing AdvancedMD patientId"
                : "Go to create a clinical note"
            }
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<NoteAddIcon />}
                onClick={goToClinicalNotes}
                disabled={disabledNoPatientForClinicalNote}
              >
                Add Clinical Note
              </Button>
            </span>
          </Tooltip>

          <Tooltip
            title={
              disabledNoPatientForInternal
                ? "Patient missing — cannot create a Care Plan"
                : "Create a Care Plan for this patient"
            }
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<PlaylistAddIcon />}
                onClick={handleAddCarePlan}
                disabled={disabledCarePlan}
              >
                Add Care Plan
              </Button>
            </span>
          </Tooltip>

          <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

          <Tooltip title={disabledMore ? "Coming soon" : "More"}>
            <span>
              <IconButton
                size="small"
                onClick={(e) => {
                  if (disabledMore) return;
                  setAnchorEl(e.currentTarget);
                }}
                disabled={disabledMore}
              >
                <MoreVertIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Menu anchorEl={anchorEl} open={menuOpen} onClose={() => setAnchorEl(null)}>
            {/* Acciones reales */}
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                handleOpenTaskForm();
              }}
            >
              <ListItemIcon>
                <AddTaskIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText primary="Add Task" secondary="Assign a task linked to this patient" />
            </MenuItem>

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                handleAddCarePlan();
              }}
            >
              <ListItemIcon>
                <PlaylistAddIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText primary="Add Care Plan" secondary="Create a Care Plan for this patient" />
            </MenuItem>

            <Divider />

            {/* Acciones placeholder — quedan disabled con tooltip "Coming soon" */}
            {quickActions.map((a) => (
              <Tooltip key={a.label} title="Coming soon" placement="left">
                <span>
                  <MenuItem disabled>{a.label}</MenuItem>
                </span>
              </Tooltip>
            ))}
          </Menu>
        </Stack>
      </Paper>

      {/* Diálogo real conectado a POST /api/ClinicalOperations/tasks */}
      <TaskFormDialog
        open={taskFormOpen}
        onClose={() => {
          setTaskFormOpen(false);
          setTaskSubmitError(null);
        }}
        onSubmit={handleSubmitTask}
        defaultValues={{
          patientName,
          cipId,
          relatedType: null,
        }}
        employees={employees}
        locations={locations}
        currentUser={currentUser}
        submitError={taskSubmitError}
      />

      {/* Snackbar de feedback (verde si OK, rojo si error) */}
      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {snackbar && (
          <Alert
            onClose={() => setSnackbar(null)}
            severity={snackbar.severity}
            variant="filled"
            elevation={6}
            sx={{
              minWidth: 300,
              borderRadius: 2.5,
              fontWeight: 600,
              ...(snackbar.severity === "success" && {
                background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                boxShadow: "0 8px 24px rgba(22, 163, 74, 0.35)",
              }),
              ...(snackbar.severity === "error" && {
                background: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
                boxShadow: "0 8px 24px rgba(220, 38, 38, 0.35)",
              }),
            }}
          >
            {snackbar.message}
          </Alert>
        )}
      </Snackbar>
    </>
  );
}
